import { useState, useRef, useEffect, useMemo, type Dispatch, type SetStateAction } from "react";
import { Button } from "../../../components/Button";
import { api } from "../../../services/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "../../../services/user-context";
import { useApp } from "../../../services/app-context";

import "react-datepicker/dist/react-datepicker.css";
import { ShowToast } from "../../../services/toast";
import { CertificationFormBasicFields } from "./CertificationFormBasicFields";
import { CertificationFormQuestions } from "./CertificationFormQuestions";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { twMerge } from "tailwind-merge";
import { getLabel } from "../utils/labels";
import { buildCallFlowSavePayload, callFlowImageDisplayName } from "../utils/callFlow";
import { getCertificationDueDateValidationError, isCertificationStartInPastOrToday, areCertificationStartAndDueBothBeforeToday } from "../utils/dates";
import { buildCertificationMetadataPayload } from "../utils/metadata";
import { isCertificationOverallSummaryEnabled } from "../../../integrations/posthog/featureFlags";
import SideModal from "./SideModal";
import { CertificationSteps } from "./CertificationSteps";
import { CertificationAssignUserStep } from "./CertificationAssignUserStep";
import { ReactComponent as ExclamationCircleIcon } from "../../../assets/icons/exclamation-circle.svg";

dayjs.extend(utc);

/** After basic fields persist (step 1), move to assign users (step 2). Idempotent if step already changed. */
function advanceToAssignUsersAfterPersist(setStep: Dispatch<SetStateAction<number>>) {
  setStep((prev) => (prev === 1 ? 2 : prev));
}

/** After assignments save (step 2), move to questions (step 3). */
function advanceToQuestionsAfterAssignments(setStep: Dispatch<SetStateAction<number>>) {
  setStep((prev) => (prev === 2 ? 3 : prev));
}


function mapApiCallFlowToForm(apiFlow: unknown): {
  id: string;
  objection: string;
  category: string;
  subtext: string;
  imageURL?: string;
  imageName?: string;
}[] {
  if (!Array.isArray(apiFlow) || apiFlow.length === 0) {
    return [{ id: crypto.randomUUID(), objection: "", category: "", subtext: "" }];
  }
  return apiFlow.map((item: any) => ({
    id: crypto.randomUUID(),
    objection: (item?.question ?? "").toString(),
    category: (item?.category ?? "").toString(),
    subtext: (item?.subtext ?? "").toString(),
    ...((item?.imageURL)
      ? { imageURL: String(item.imageURL) }
      : {}),
    ...(item?.imageName ? { imageName: String(item.imageName) } : {}),
  }));
}

/** Call-flow rows for PUT `editCertification` (imageURL / imageName per API contract). */
function buildCallFlowForEditApi(callFlow: any[]) {
  return callFlow.map((item: any) => {
    const row: Record<string, unknown> = {
      question: (item.objection ?? item.question ?? "").toString(),
      category: item.category,
      subtext: item.subtext ?? "",
    };
    const imageURL = item.imageURL;
    if (imageURL) {
      row.imageURL = typeof imageURL === "string" ? imageURL : String(imageURL);
      const imageName = callFlowImageDisplayName(item);
      if (imageName) row.imageName = imageName;
    }
    return row;
  });
}

function buildEditCertificationPayload(
  form: any,
  userInfo: { id?: string } | undefined,
  options: { includeCallFlow: boolean }
) {
  const base = buildCertificationMetadataPayload(form, userInfo);
  if (!options.includeCallFlow) return base;
  return {
    ...base,
    callFlow: buildCallFlowForEditApi(form.callFlow || []),
  };
}

/** Metadata for new-cert step 1 (POST/PATCH): no assignees here — step 2 uses `patchCertificationAssignments`. */
function buildNewCertStep1MetadataPayload(form: any, userInfo: { id?: string } | undefined) {
  return buildCertificationMetadataPayload(form, userInfo);
}


export function Field({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="text-[13px] font-medium text-text-primary">{label}</label>
      {children}
    </div>
  );
}

export const CertificationFormModal = ({
  onClose,
  isOpen,
  onSuccess,
  certification,
}: {
  onClose: () => void;
  isOpen: boolean;
  onSuccess: () => void;
  certification?: any;
}) => {
  const queryClient = useQueryClient();
  const { userInfo } = useUser() as any;
  const { tenantProfile, certificationAssets } = useApp();
  const labels = certificationAssets?.labels || {};
  const certificationSingular = getLabel("certification.singular", labels);
  const sessionSingular = getLabel("session.singular", labels);

  const isEdit = !!certification;
  const {
    data: certificationDetails,
    isSuccess: certificationDetailsReady,
    dataUpdatedAt: certificationDetailsUpdatedAt,
  } = useQuery({
    queryKey: ["certification-details", certification?.id],
    queryFn: () => api.getCertificationDetails(certification?.id!),
    enabled: !!certification?.id && isOpen,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const {
    data: certificationCallFlowDetails,
    isSuccess: certificationCallFlowReady,
    dataUpdatedAt: certificationCallFlowDetailsUpdatedAt,
  } = useQuery({
    queryKey: ["certification-call-flow-details", certification?.id],
    queryFn: () => api.getCertificationCallFlowDetails(certification?.id!),
    enabled: !!certification?.id && isOpen,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isEditBasicFieldsLoading =
    isEdit && !!certification?.id && (!certificationDetailsReady || !certificationCallFlowReady);
  const { mutate, isLoading, error } = useMutation({
    mutationFn: (data: any) => api.createCertification(data),
    onSuccess: (data: any) => {
      if (data?.data?.certificationID) {
        setForm((prev: any) => ({
          ...prev,
          certificationId: data?.data?.certificationID,
        }));
        advanceToAssignUsersAfterPersist(setStep);
      } else {
        ShowToast({
          message: `Error creating ${certificationSingular.toLowerCase()}`,
          type: "error",
        });
      }
    },
    onError: (error: any) => {
      ShowToast({
        message: `Failed to create ${certificationSingular.toLowerCase()}`,
        type: "error",
      });
    },
  });
  const { mutate: saveCallFlow, isLoading: isLoadingSaveCallFlow } = useMutation({
    mutationFn: (data: any) => api.saveCallFlow(data.certificationId, data.data),
    onSuccess: (_data: unknown, variables: { certificationId: string; data: unknown }) => {
      ShowToast({
        message: "Call flow saved successfully",
        type: "success",
      });
      const id = variables.certificationId;
      queryClient.invalidateQueries({ queryKey: ["certification-details", id] });
      queryClient.invalidateQueries({ queryKey: ["certification-call-flow-details", id] });
      onClose();
      onSuccess();
    },
    onError: (error: any) => {
      ShowToast({
        message: "Error saving call flow",
        type: "error",
      });
    },
  });
  const { mutate: updateCertification, isLoading: isLoadingUpdateCertification } = useMutation({
    mutationFn: ({
      certificationId,
      data,
      afterUpdate,
    }: {
      certificationId: string;
      data: any;
      afterUpdate?: "edit-advance" | "edit-finish" | "create-advance";
    }) =>
      afterUpdate === "create-advance"
        ? api.updateCertification(certificationId, data)
        : api.editCertification(certificationId, data),
    onSuccess: (_data: unknown, variables) => {
      ShowToast({
        message: `${certificationSingular} updated successfully`,
        type: "success",
      });
      const id = variables.certificationId;
      // Do not invalidate details/call-flow on `edit-advance`: refetch updates `dataUpdatedAt` and
      // re-runs the edit prefill effect while the user is on step 2, wiping in-progress call flow edits.
      if (variables.afterUpdate !== "edit-advance") {
        queryClient.invalidateQueries({ queryKey: ["certification-details", id] });
        queryClient.invalidateQueries({ queryKey: ["certification-call-flow-details", id] });
      }
      if (variables.afterUpdate === "edit-finish") {
        onClose();
        onSuccess();
        return;
      }
      // Edit restricted (due-date-only): save step 1 metadata, refresh list, advance to read-only step 2.
      if (variables.afterUpdate === "edit-advance") {
        onSuccess();
        setStep(2);
        return;
      }
      // Create: 3 steps — metadata save (step 1) → assign users (step 2).
      if (variables.afterUpdate === "create-advance") {
        advanceToAssignUsersAfterPersist(setStep);
      }
    },
    onError: (error: any, variables) => {
      if (variables.afterUpdate === "edit-finish") {
        if (error?.response?.status === 422) {
          const errors = error?.response?.data?.errors || {};
          const errorKeys = Object.keys(errors);
          const step2Keys = new Set(["question", "category"]);
          const isStep2Only = errorKeys.length > 0 && errorKeys.every((k) => step2Keys.has(k));
          setStep(isStep2Only ? 2 : 1);
        }
        ShowToast({
          message:
            error?.response?.status === 422
              ? "Failed to save. Please review and try again."
              : `Failed to save ${certificationSingular.toLowerCase()}. Please try again.`,
          type: "error",
        });
        return;
      }
      const errorMessage = error?.response?.data?.message || `Failed to update ${certificationSingular.toLowerCase()}`;
      ShowToast({
        message: errorMessage,
        type: "error",
      });
    },
  });
  const { mutate: assignCertificationUsers, isLoading: isLoadingAssignUsers } = useMutation({
    mutationFn: (data: { certificationId: string; users: string[] }) =>
      api.patchCertificationAssignments(data.certificationId, { users: data.users }),
    onSuccess: () => {
      ShowToast({
        message: "User assignments saved",
        type: "success",
      });
      // Create flow only (edit has no assign step).
      advanceToQuestionsAfterAssignments(setStep);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save user assignments";
      ShowToast({
        message: errorMessage,
        type: "error",
      });
    },
  });
  const getInitialForm = () => ({
    primer: "",
    isActive: 1,
    certificationAttemptsRequired: 1,
    certificationAttemptsAllowed: 1,
    certificationStartDate: null,
    certificationDueDate: null,
    certificationName: "",
    regionId: "",
    territoryId: "",
    isLocationBased: 0,
    isProductBased: 0,
    scoringMethod: "average",
    salesAidFile: "",
    code: "",
    userList: [] as { label: string; value: string }[],
    callFlow: [{ id: crypto.randomUUID(), objection: "", category: "", subtext: "" }],
    salesAidFiles: [],
    productName: tenantProfile?.product,
  });

  const [step, setStep] = useState<number>(1);
  const [form, setForm] = useState<any>(getInitialForm);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const showOverallSummary = isCertificationOverallSummaryEnabled();

  const { data: categoriesData } = useQuery({
    queryKey: ["certification-categories", form.salesAidFile],
    queryFn: () => api.getCertificationCategoriesByDocument(form.salesAidFile),
    enabled: !!form.salesAidFile,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
  const categories = categoriesData?.data || [];

  const prevOpenRef = useRef(false);
  /** Last query `dataUpdatedAt` pair applied to the form — allows re-prefill after refetch/reopen (avoids one-shot guard blocking fresh data). */
  const lastEditPrefillDataUpdatedAtRef = useRef<{ detailsAt: number; callFlowAt: number } | null>(null);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setStep(1);
      setForm(getInitialForm());
      lastEditPrefillDataUpdatedAtRef.current = null;
    }
    if (!isOpen) {
      lastEditPrefillDataUpdatedAtRef.current = null;
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isEdit || !certification?.id) return;
    lastEditPrefillDataUpdatedAtRef.current = null;
  }, [isEdit, certification?.id]);

  const { data: salesAidData } = useQuery({
    queryKey: ["sales-aid-certification-files"],
    queryFn: () => api.getSalesAidCertificationFiles(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const salesAidFiles = salesAidData?.data || [];

  const salesAidFileOptions = useMemo(() => {
    return salesAidFiles.map((file) => ({
      label: file.file_name,
      value: file.id,
      code: file.code,
    }));
  }, [salesAidFiles]);

  useEffect(() => {
    if (!isOpen || !isEdit || !certification?.id || !certificationDetailsReady || !certificationCallFlowReady) {
      return;
    }
    // Only hydrate from queries on step 1; after advancing, the form (including call flow) is authoritative.
    if (step !== 1) {
      return;
    }
    const pair = {
      detailsAt: certificationDetailsUpdatedAt,
      callFlowAt: certificationCallFlowDetailsUpdatedAt,
    };
    const prev = lastEditPrefillDataUpdatedAtRef.current;
    if (prev && prev.detailsAt === pair.detailsAt && prev.callFlowAt === pair.callFlowAt) {
      return;
    }
    const d = certificationDetails?.data;
    if (!d) return;
    lastEditPrefillDataUpdatedAtRef.current = pair;
    const salesAidId = d.salesAidFile != null && d.salesAidFile !== "" ? String(d.salesAidFile) : "";
    const fileMeta = salesAidFiles.find((f: any) => String(f.id) === String(d.salesAidFile));
    const apiFlow = certificationCallFlowDetails?.data?.callFlow;
    setForm({
      ...getInitialForm(),
      certificationId: d.certificationID ?? certification.id,
      certificationName: d.certificationName ?? "",
      certificationStartDate: d.certificationStartDate ? dayjs.utc(d.certificationStartDate).toDate() : null,
      certificationDueDate: d.certificationDueDate ? dayjs.utc(d.certificationDueDate).toDate() : null,
      certificationAttemptsRequired: d.certificationAttemptsRequired ?? 1,
      certificationAttemptsAllowed: d.certificationAttemptsAllowed ?? 1,
      primer: d.primer ?? "",
      salesAidFile: salesAidId,
      code: fileMeta?.code ?? "",
      isActive: d.isActive ? 1 : 0,
      productName: tenantProfile?.product ?? getInitialForm().productName,
      userList: [],
      callFlow: mapApiCallFlowToForm(apiFlow),
    });
  }, [
    isOpen,
    isEdit,
    step,
    certification?.id,
    certificationDetailsReady,
    certificationCallFlowReady,
    certificationDetailsUpdatedAt,
    certificationCallFlowDetailsUpdatedAt,
    certificationDetails,
    certificationCallFlowDetails,
    salesAidFiles,
    tenantProfile?.product,
  ]);

  useEffect(() => {
    if (!isEdit || !form.salesAidFile || form.code) return;
    const fileMeta = salesAidFiles.find((f: any) => String(f.id) === String(form.salesAidFile));
    if (fileMeta?.code) {
      setForm((prev: any) => ({ ...prev, code: fileMeta.code }));
    }
  }, [isEdit, form.salesAidFile, form.code, salesAidFiles]);

  const handleSave = async () => {
    try {
      const payload = buildNewCertStep1MetadataPayload(form, userInfo);
      if (form.certificationId) {
        updateCertification({
          certificationId: form.certificationId,
          data: payload,
          afterUpdate: "create-advance",
        });
      } else {
        mutate(payload);
      }
    } catch (error) {
      ShowToast({
        message: "Error creating certification.",
        type: "error",
      });
    }
  };

  const addUserAssignment = () => {
    if (isEdit) return;
    const certificationId = form.certificationId;
    if (!certificationId) {
      ShowToast({
        message: `Save ${certificationSingular.toLowerCase()} details before assigning users`,
        type: "error",
      });
      return;
    }
    const users = (form.userList || []).map((u: { value: string }) => u.value);
    assignCertificationUsers({ certificationId, users });
  };

  const handleCreateCertification = async () => {
    try {
      saveCallFlow({ certificationId: form.certificationId, data: buildCallFlowSavePayload(form.callFlow) });
    } catch (error) {
      ShowToast({
        message: "Error creating certification",
        type: "error",
      });
    }
  };

  const handleUpdateCertificationMetadata = () => {
    const id = certification?.id ?? form.certificationId;
    if (!id) return;
    const payload = buildEditCertificationPayload(form, userInfo, { includeCallFlow: false });
    updateCertification({ certificationId: id, data: payload, afterUpdate: "edit-advance" });
  };

  const handleSaveCallFlow = () => {
    const id = certification?.id ?? form.certificationId;
    if (!id) return;
    try {
      const payload = buildEditCertificationPayload(form, userInfo, { includeCallFlow: true });
      updateCertification({ certificationId: id, data: payload, afterUpdate: "edit-finish" });
    } catch (error) {
      ShowToast({
        message: "Error saving call flow",
        type: "error",
      });
    }
  };

  useEffect(() => {
    if (tenantProfile) {
      setForm((prev) => ({
        ...prev,
        productName: tenantProfile?.product,
      }));
    }
  }, [tenantProfile]);

  const isCallFlowValid = useMemo(() => {
    if (!form.callFlow?.length) return false;
    return form.callFlow.every((item: any) => {
      const objection = (item.objection ?? item.question ?? "").toString().trim();
      const category = (item.category ?? "").toString().trim();
      return objection.length > 0 && category.length > 0;
    });
  }, [form.callFlow]);

  // Lock states must reflect the persisted DB dates, not the in-progress form values.
  // If we used form.certificationStartDate, changing the date in the form would
  // immediately flip the mode and trigger an unintended API call on Continue.
  const originalStartDate = certificationDetails?.data?.certificationStartDate ?? null;
  const originalDueDate = certificationDetails?.data?.certificationDueDate ?? null;

  const editFullyLockedPast = useMemo(
    () =>
      isEdit &&
      areCertificationStartAndDueBothBeforeToday(originalStartDate, originalDueDate),
    [isEdit, originalStartDate, originalDueDate]
  );

  const editRestrictedToDueDateOnly = useMemo(
    () =>
      isEdit &&
      !editFullyLockedPast &&
      isCertificationStartInPastOrToday(originalStartDate),
    [isEdit, editFullyLockedPast, originalStartDate]
  );

  const handleContinue = () => {
    if (editFullyLockedPast) {
      return;
    }
    const dueDateError = getCertificationDueDateValidationError(
      form.certificationStartDate,
      form.certificationDueDate
    );
    if (dueDateError && (step === 1 || (isEdit && step === 2))) {
      setErrorMessage(dueDateError);
      return;
    } else {
      setErrorMessage(null);
    }

    if (isEdit) {
      if (step === 2) {
        handleSaveCallFlow();
      } else {
        // When only the due date is editable, save metadata immediately on step 1 continue
        // (document cannot change so no broken-state risk). Otherwise defer all saves to step 2.
        if (editRestrictedToDueDateOnly) {
          handleUpdateCertificationMetadata();
        } else {
          setStep(2);
        }
      }
    } else {
      if (step === 3) {
        handleCreateCertification();
      } else if (step === 1) {
        handleSave();
      } else if (step === 2) {
        addUserAssignment();
      }
    }
  };

  const lastStep = isEdit ? 2 : 3;

  const isDisabled = useMemo(() => {
    if (isEdit && editFullyLockedPast) {
      return true;
    }
    if (isEdit && step === 1 && isEditBasicFieldsLoading) {
      return true;
    }
    if (isEdit && step === 2) {
      return !isCallFlowValid;
    }
    if (!isEdit && step === 2) {
      return !form.userList?.length;
    }
    if (!isEdit && step === 3) {
      return !isCallFlowValid;
    }
    const invalidAttemptDetails = (form: any) => {
      // if overall summary is enabled then only show this validation
      if (showOverallSummary) {
        // true means not valid attempt details
        if (!form.certificationAttemptsRequired || !form.certificationAttemptsAllowed) return true;
        if (Number(form.certificationAttemptsRequired) > Number(form.certificationAttemptsAllowed)) return true;
      }
      return false;
    }
    return (
      !form.certificationName ||
      !form.certificationStartDate ||
      !form.certificationDueDate ||
      !form.certificationAttemptsAllowed ||
      !form.productName ||
      !form.salesAidFile ||
      !form.primer ||
      invalidAttemptDetails(form)
    );
  }, [form, step, isCallFlowValid, showOverallSummary, isEdit, editFullyLockedPast, isEditBasicFieldsLoading]);

  const loading =
    isLoading ||
    isLoadingSaveCallFlow ||
    isLoadingUpdateCertification ||
    isLoadingAssignUsers;

  return (
    <SideModal
      show={isOpen}
      toggle={onClose}
      title={
        isEdit
          ? `Edit ${getLabel("certificationName.singular", labels)}`
          : `New ${getLabel("certificationName.singular", labels)}`
      }
      containerClass="flex flex-col"
    >
      <div className="w-full my-5">
      <CertificationSteps currentStep={step} isEdit={isEdit} />
      </div>
      <div className="mb-4">
        <div className="text-text-primary text-[15px] mt-1">
          {isEdit
            ? `You are editing the details of the ${sessionSingular}.`
            : `Fill out the form below to create a new ${getLabel("certificationName.singular", labels)}`}
        </div>
      </div>
      {isEdit && editFullyLockedPast && (
        <div
          className="flex items-start gap-2 px-3 py-2.5 mb-3 bg-amber-50 border border-amber-200 rounded-[10px]"
          role="alert"
        >
          <ExclamationCircleIcon className="w-6 h-6 shrink-0 [&_path]:fill-amber-600" />
          <span className="text-[15px] text-amber-950 leading-snug">
            The start and due dates are in the past. You cannot change any fields or questions for this{" "}
            {getLabel("certificationName.singular", labels).toLowerCase()}.
          </span>
        </div>
      )}
      
      {!!errorMessage && (<div id="error-message" className="flex items-center gap-2 px-2.5 py-2 mb-2 bg-red-50 rounded-[10px]">
          <ExclamationCircleIcon className="w-6 h-6" />
          <span className="text-black text-base">{errorMessage}</span>
        </div>
      )}
      {step === 2 && isEdit && editRestrictedToDueDateOnly && (
        <div id="info-message" className="flex items-center gap-2 px-2.5 py-2 mb-2 bg-[#FEFCE8] rounded-[10px]">
          <ExclamationCircleIcon className="w-6 h-6 [&_path]:fill-[#CA8A04]" />
          <span className="text-black text-base">You can not edit questions after start date.</span>
        </div>
      )}
      <div className="flex flex-col gap-4 max-h-[75vh] overflow-auto mb-4">
        {!isEdit ? <>
          {step === 1 && (
            <CertificationFormBasicFields
              form={form}
              setForm={setForm}
              salesAidFiles={salesAidFiles}
              salesAidFileOptions={salesAidFileOptions}
            />
          )}
          {step === 2 && <CertificationAssignUserStep form={form} setForm={setForm} />}
          {step === 3 && <CertificationFormQuestions form={form} setForm={setForm} categories={categories} />}
        </>:
        <>
          {step === 1 && (
            <CertificationFormBasicFields
              isEdit
              isEditDataLoading={isEditBasicFieldsLoading}
              restrictToDueDateOnly={editRestrictedToDueDateOnly}
              fullyLocked={editFullyLockedPast}
              form={form}
              setForm={setForm}
              salesAidFiles={salesAidFiles}
              salesAidFileOptions={salesAidFileOptions}
            />
          )}
          {step === 2 && (
            <CertificationFormQuestions
              form={form}
              setForm={setForm}
              categories={categories}
              callFlowDisabled={editRestrictedToDueDateOnly || editFullyLockedPast}
            />
          )}
        </>}

      </div>
      <div className={twMerge("flex justify-between w-full flex-1 items-end", step === 1 ? "!justify-end" : "")}>
        {step !== 1 && <Button variant="primary" className="!bg-[#D2D2D2] !h-[37px] w-[117px] !text-lg !font-normal !text-black !rounded-full hover:opacity-80 flex items-center gap-1" disabled={loading} onClick={() => setStep(prev => prev - 1)}>

          Back
        </Button>}
        {step === 2 && isEdit && editRestrictedToDueDateOnly ? 
        <Button
          onClick={onClose}
          variant="primary"
          className={`h-[37px] !w-auto !px-3 !text-lg !font-normal !rounded-full`}
        >
          Close
        </Button>
        : <Button
          onClick={handleContinue}
          variant="primary"
          className={`h-[37px] !w-auto !px-3 !text-lg !font-normal !rounded-full`}
          loading={loading}
          disabled={isDisabled || loading || (step === 2 && isEdit && editRestrictedToDueDateOnly)}
        >
          {step === lastStep
            ? isEdit
              ? `Save ${getLabel("certification.singular", labels)}`
              : `Create ${getLabel("certification.singular", labels)}`
            : "Continue"}
        </Button>}
      </div>
    </SideModal>
  );
};
